import findKey from 'lodash-es/findKey'

import { EMPTY_OBJECT, EMPTY_STRING } from '../constants/common'
import {
  AUDIO_TYPES,
  DOC_TYPES,
  FILE_FOLDER_TYPE,
  FILE_GIF_TYPE,
  FILE_MB_SIZE,
  FILE_NAME_FORBIDDEN_CHARS_REGEXP,
  FILE_NAME_MAX_LENGTH,
  FILE_NAME_WHITESPACE_REGEXP,
  FILE_TYPE_ALIASES,
  IMAGE_FIT_TYPES,
  IMAGE_TYPES,
  PDF_TEMPLATE_TYPE,
  PDF_TYPES,
  PPT_TYPES,
  VIDEO_TYPES,
  XLS_TYPES,
  XML_TYPES,
} from '../constants/file'

export function isImageFitTypeContainAll(fit) {
  return fit === IMAGE_FIT_TYPES.containAll
}

export function isImageFitTypeContainVertical(fit) {
  return fit === IMAGE_FIT_TYPES.containVertical
}

export function getFileType(file) {
  return file.template
    ? PDF_TEMPLATE_TYPE
    : findKey(FILE_TYPE_ALIASES, value =>
        value.includes(file.mime_type || file.type)
      )
}

export function isPdfFile(data) {
  return PDF_TYPES.includes(data.mime_type || data.type)
}

export function isDocFile(data) {
  return DOC_TYPES.includes(data.mime_type || data.type)
}

export function isXlsFile(data) {
  return XLS_TYPES.includes(data.mime_type || data.type)
}

export function isXmlFile(data) {
  return XML_TYPES.includes(data.mime_type || data.type)
}

export function isGifFile(data) {
  return FILE_GIF_TYPE === data?.mime_type || FILE_GIF_TYPE === data?.type
}

export function isImageFile(data) {
  return IMAGE_TYPES.includes(data.mime_type || data.type)
}

export function isVideoFile(data) {
  return VIDEO_TYPES.includes(data.mime_type || data.type)
}

export function isPptFile(data) {
  return PPT_TYPES.includes(data.mime_type || data.type)
}

export function isMediaFile(data) {
  return isImageFile(data) || isVideoFile(data)
}

export function isAudioFile(data) {
  return AUDIO_TYPES.includes(data.mime_type || data.type)
}

export function isFolderFile(data) {
  return data?.type === FILE_FOLDER_TYPE
}

export function isSystemFolder(data) {
  return isFolderFile(data) && data?.owner?.is_system
}

export function isNoSystemFolder(data) {
  return isFolderFile(data) && !data?.owner?.is_system
}

export function convertBlobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onerror = reject

    reader.onload = () => {
      resolve(reader.result)
    }
    reader.readAsDataURL(blob)
  })
}

export async function formatFilesToBase64(files) {
  const pArray = files.map(async file => {
    const base64File = await convertBlobToBase64(file)

    return {
      blob: file,
      filename: file.name,
      id: file.name,
      name: file.name,
      origin: base64File,
      size: file.size,
      type: file.type,
    }
  })
  const processed = await Promise.all(pArray)

  return processed
}

export function getBytesFromMegabytes(megabytes) {
  return megabytes * FILE_MB_SIZE
}

export function getMegabytesFromBytes(bytes) {
  return bytes / FILE_MB_SIZE
}

export function getFilesTotalSize(files) {
  return files.reduce((acc, item) => {
    return acc + item.size || 0
  }, 0)
}

export function getImageFileThumbnail({ file, size }) {
  if (size && !file.thumbnails[size]) {
    console.warn('Requested thumbnail was not found check data')
  }

  return size ? file.thumbnails[size] : file.thumbnail
}

export function getImageFilePreview(file) {
  return file.preview
}

// The backend keys `thumbnails` by width and scales each one to that width
export function getImageFileThumbnailsSrcSet(file) {
  return Object.entries(file.thumbnails ?? EMPTY_OBJECT)
    .map(([width, url]) => `${url} ${width}w`)
    .join(', ')
}

export function getHasFilesToShow({ files, filesError, filesUploading }) {
  return files.length > 0 || filesUploading.length > 0 || filesError.length > 0
}

export function getSafeFileName(name) {
  return name
    .replace(FILE_NAME_FORBIDDEN_CHARS_REGEXP, ' ')
    .replace(FILE_NAME_WHITESPACE_REGEXP, ' ')
    .trim()
}

export function getFileNameWithExtension(name, extension) {
  const baseName = name.slice(0, FILE_NAME_MAX_LENGTH - extension.length).trim()

  return `${baseName}${extension}`
}

// Saves a file without navigating away (unlike window.open): the backend
// serves the URL with Content-Disposition: attachment, so the anchor click
// only triggers the browser download. Pass fileName for data: / blob: URLs,
// which have no headers to name the file.
export function downloadFileByUrl(url, fileName = EMPTY_STRING) {
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = fileName

  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}
